import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { buildingsApi } from '../api/buildingsApi';
import { pdfExportsApi } from '../api/pdfExportsApi';
import { apartmentStatsApi, ApartmentStats, NotificationHistory } from '../api/apartmentStatsApi';
import { apartmentsApi, Apartment } from '../api/apartmentsApi';
import { feeCategoriesApi, FeeCategory } from '../api/feeCategoriesApi';
import { useLang } from '../contexts/LangContext';
import { useAuth } from '../contexts/AuthContext';
import {
  applyDefaultBuildingSelection,
  persistSelectedBuilding,
} from '../utils/buildingSelection';
import ApartmentGroupMultiSelect from '../components/apartments/ApartmentGroupMultiSelect';
import ApartmentMultiSelect from '../components/apartments/ApartmentMultiSelect';
import FeeCategoryMultiSelect from '../components/fee-categories/FeeCategoryMultiSelect';
import FeeNotificationDeliveryModal from '../components/modals/FeeNotificationDeliveryModal';
import { getApiErrorMessage } from '../utils/apiErrors';
import {
  billingPeriodSlashToDash,
  generateFeeNoticeExportPeriodOptions,
} from '../utils/billingPeriods';
import { getCurrentBillingPeriodValue } from '../utils/billingPeriodPicker';

interface Building {
  id: string;
  name: string;
  address?: string;
  serviceFeeDay: number;
  autoSendEmailReminder: boolean;
  paymentFromDay: number;
  paymentToDay: number;
  isActive: boolean;
  createdAt: string;
  apartmentCount?: number;
}

export const SendFeeNotification: React.FC = () => {
  const { t } = useLang();
  const { user } = useAuth();
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [selectedBuilding, setSelectedBuilding] = useState<Building | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [apartmentStats, setApartmentStats] = useState<ApartmentStats | null>(null);
  const [notificationHistory, setNotificationHistory] = useState<NotificationHistory[]>([]);
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [apartmentGroups, setApartmentGroups] = useState<Array<{ id: string; name: string }>>([]);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [selectedApartmentIds, setSelectedApartmentIds] = useState<string[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [noticeIssueLabel, setNoticeIssueLabel] = useState('');
  const [feeCategories, setFeeCategories] = useState<FeeCategory[]>([]);
  const [selectedFeeCategoryIds, setSelectedFeeCategoryIds] = useState<string[]>([]);
  const [onlyOutstanding, setOnlyOutstanding] = useState(true);
  const [includeAllUnpaidOnNotice, setIncludeAllUnpaidOnNotice] = useState(false);
  const [billingPeriods, setBillingPeriods] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);

  useEffect(() => {
    loadBuildings();
    setBillingPeriods(generateFeeNoticeExportPeriodOptions());
    setSelectedPeriod(billingPeriodSlashToDash(getCurrentBillingPeriodValue()));
  }, [user?.id]);

  const loadBuildings = async () => {
    try {
      setLoading(true);
      const buildingsData = await buildingsApi.getBuildings();
      setBuildings(buildingsData);
      const defaultBuildingId = applyDefaultBuildingSelection(buildingsData, user, '');
      const initialBuilding = buildingsData.find((b) => b.id === defaultBuildingId);
      if (initialBuilding) {
        handleBuildingSelect(initialBuilding, false);
      }
    } catch (error) {
      console.error('Failed to load buildings:', error);
      toast.error('Có lỗi xảy ra khi tải danh sách chung cư');
    } finally {
      setLoading(false);
    }
  };

  const handleBuildingSelect = (building: Building, persist = true) => {
    if (persist) {
      persistSelectedBuilding(user?.id, building.id);
    }
    setSelectedBuilding(building);
    setSelectedGroupIds([]);
    setSelectedApartmentIds([]);
    loadApartmentGroups(building.id);
    loadApartments(building.id);
    loadFeeCategories(building.id);
    loadApartmentStats(building.id);
    loadNotificationHistory(building.id);
  };

  const loadFeeCategories = async (buildingId: string) => {
    try {
      const categories = await feeCategoriesApi.getAll(buildingId);
      const active = categories.filter((cat) => cat.isActive !== false);
      setFeeCategories(active);
      setSelectedFeeCategoryIds(active.map((cat) => cat.id));
    } catch (error) {
      console.error('Failed to load fee categories:', error);
      setFeeCategories([]);
      setSelectedFeeCategoryIds([]);
    }
  };

  const loadApartments = async (buildingId: string) => {
    try {
      const apartmentList = await apartmentsApi.getApartments({ buildingId });
      setApartments(apartmentList.filter((apt) => apt.status === 'ACTIVE'));
    } catch (error) {
      console.error('Failed to load apartments:', error);
      setApartments([]);
    }
  };

  const loadApartmentGroups = async (buildingId: string) => {
    try {
      const groups = await apartmentsApi.getApartmentGroups(buildingId);
      setApartmentGroups(
        groups
          .filter((group) => group.status === 'ACTIVE')
          .map((group) => ({ id: group.id, name: group.name })),
      );
    } catch (error) {
      console.error('Failed to load apartment groups:', error);
      setApartmentGroups([]);
    }
  };

  const loadApartmentStats = async (buildingId: string) => {
    try {
      setLoadingStats(true);
      const stats = await apartmentStatsApi.getApartmentStats(buildingId);
      setApartmentStats(stats);
    } catch (error) {
      console.error('Failed to load apartment stats:', error);
      setApartmentStats(null);
    } finally {
      setLoadingStats(false);
    }
  };

  const loadNotificationHistory = async (buildingId: string) => {
    try {
      setLoadingHistory(true);
      const history = await apartmentStatsApi.getNotificationHistory(buildingId);
      setNotificationHistory(history);
    } catch (error) {
      console.error('Failed to load notification history:', error);
      setNotificationHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const openDeliveryModal = () => {
    if (!selectedBuilding) {
      toast.error('Vui lòng chọn chung cư trước khi gửi thông báo phí');
      return;
    }
    if (!selectedPeriod) {
      toast.error('Vui lòng chọn kỳ phí trên thông báo');
      return;
    }
    if (feeCategories.length > 0 && selectedFeeCategoryIds.length === 0) {
      toast.error('Vui lòng chọn ít nhất một danh mục phí trên thông báo');
      return;
    }
    setShowDeliveryModal(true);
  };

  const handleConfirmSend = async (deliveryMethods: string[]) => {
    if (!selectedBuilding) {
      return;
    }

    const allCategoriesSelected =
      feeCategories.length === 0 ||
      selectedFeeCategoryIds.length === feeCategories.length;

    try {
      setSending(true);
      const response = await pdfExportsApi.sendBuildingFeeNotices({
        buildingId: selectedBuilding.id,
        billingPeriod: selectedPeriod,
        ...(noticeIssueLabel.trim()
          ? { noticeIssueLabel: noticeIssueLabel.trim() }
          : {}),
        onlyOutstanding,
        includeAllUnpaidOnNotice,
        apartmentGroupIds:
          selectedApartmentIds.length > 0
            ? undefined
            : selectedGroupIds.length > 0
              ? selectedGroupIds
              : undefined,
        apartmentIds: selectedApartmentIds.length > 0 ? selectedApartmentIds : undefined,
        ...(!allCategoriesSelected && selectedFeeCategoryIds.length > 0
          ? { feeCategoryIds: selectedFeeCategoryIds }
          : {}),
        deliveryMethods: deliveryMethods as ('EMAIL' | 'APP')[],
      });

      if (response.success) {
        toast.success(
          `✅ ${t('sendFeeNotification')} thành công cho chung cư ${selectedBuilding.name}! (${response.successCount}/${response.noticeCount})`,
        );
        loadApartmentStats(selectedBuilding.id);
        loadNotificationHistory(selectedBuilding.id);
        setShowDeliveryModal(false);
      } else {
        toast.error(
          response.errors?.join('; ') ||
            response.message ||
            'Gửi thông báo phí thất bại',
        );
      }
    } catch (error: unknown) {
      console.error('Error sending fee notification:', error);
      toast.error(getApiErrorMessage(error, 'Có lỗi xảy ra khi gửi thông báo phí'));
    } finally {
      setSending(false);
    }
  };

  const selectedPeriodLabel =
    billingPeriods.find((p) => p.value === selectedPeriod)?.label || selectedPeriod;

  const formatNotificationMethod = (method: string) => {
    if (method === 'Email') return t('emailNotificationMethod');
    if (method === 'App Push') return t('appPushNotificationMethod');
    if (method.includes('Email') && method.includes('App Push')) return t('bothNotificationMethods');
    return method;
  };

  const getCurrentMonth = () => selectedPeriodLabel || '—';

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Breadcrumb */}
      <nav className="flex mb-6 text-sm">
        <span className="text-gray-500">🏠</span>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-500">{t('buildings').toUpperCase()}</span>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-cyan-600 font-medium">{t('sendFeeNotification')}</span>
      </nav>

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('sendFeeNotification')}</h1>
        <p className="text-gray-600">
          {t('monthlyServiceFeeNotification')} - {getCurrentMonth()}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Building Selection */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('selectBuilding')}</h2>
            
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {buildings.map((building) => (
                <div
                  key={building.id}
                  onClick={() => handleBuildingSelect(building)}
                  className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md ${
                    selectedBuilding?.id === building.id
                      ? 'border-blue-500 bg-blue-50 shadow-md'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{building.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">{building.address || t('noAddress')}</p>
                      <div className="flex items-center mt-2 space-x-4 text-xs text-gray-600">
                        <span>{t('serviceFeeDay')}: {building.serviceFeeDay}</span>
                        <span className={`px-2 py-1 rounded-full ${
                          building.autoSendEmailReminder 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          Email: {building.autoSendEmailReminder ? t('enabled') : t('disabled')}
                        </span>
                      </div>
                    </div>
                    {selectedBuilding?.id === building.id && (
                      <div className="flex-shrink-0 ml-3">
                        <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {buildings.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m0 0h2M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <p>{t('noBuildingsYet')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Building Information & Action */}
        <div className="lg:col-span-2">
          {selectedBuilding ? (
            <div className="space-y-6">
              {/* Building Details */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('buildingInfoSection')}</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{selectedBuilding.name}</h3>
                    <p className="text-gray-600 mb-4">{selectedBuilding.address || 'Chưa có địa chỉ'}</p>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('serviceFeeDayText')}:</span>
                        <span className="font-medium">{selectedBuilding.serviceFeeDay}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('billingPeriodText')}:</span>
                        <span className="font-medium">{selectedBuilding.paymentFromDay} - {selectedBuilding.paymentToDay}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('autoSendEmailReminderText')}:</span>
                        <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                          selectedBuilding.autoSendEmailReminder 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {selectedBuilding.autoSendEmailReminder ? t('enabled') : t('disabled')}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m0 0h2M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      <p className="text-sm text-gray-600">{t('status')}: 
                        <span className={`ml-1 font-medium ${selectedBuilding.isActive ? 'text-green-600' : 'text-red-600'}`}>
                          {selectedBuilding.isActive ? t('active') : t('inactive')}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Apartment Information */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('apartmentInfo')}</h2>
                
                {loadingStats ? (
                  <div className="flex justify-center items-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  </div>
                ) : apartmentStats ? (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">{apartmentStats.totalApartments}</div>
                      <div className="text-sm text-gray-600 mt-1">{t('totalApartments')}</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">{apartmentStats.apartmentsWithEmail}</div>
                      <div className="text-sm text-gray-600 mt-1">{t('apartmentsWithEmail')}</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">{apartmentStats.apartmentsWithPhone}</div>
                      <div className="text-sm text-gray-600 mt-1">{t('apartmentsWithPhone')}</div>
                    </div>
                    <div className="text-center p-4 bg-indigo-50 rounded-lg">
                      <div className="text-2xl font-bold text-indigo-600">{apartmentStats.apartmentsWithAppLinked ?? 0}</div>
                      <div className="text-sm text-gray-600 mt-1">{t('apartmentsWithAppLinked')}</div>
                    </div>
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                      <div className="text-2xl font-bold text-red-600">{apartmentStats.apartmentsWithOutstandingFees}</div>
                      <div className="text-sm text-gray-600 mt-1">{t('apartmentsWithOutstandingFees')}</div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>Không có dữ liệu thống kê</p>
                  </div>
                )}
              </div>

              {/* Action Panel */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('actionNotificationSection')}</h2>

                <div className="mb-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('selectBillingPeriodToExport')}
                    </label>
                    <select
                      value={selectedPeriod}
                      onChange={(e) => setSelectedPeriod(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">{t('selectBillingPeriod')}</option>
                      {billingPeriods.map((period) => (
                        <option key={period.value} value={period.value}>
                          {period.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Lần thông báo
                      <span className="text-gray-400 font-normal ml-1">(tùy chọn)</span>
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={noticeIssueLabel}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '');
                        setNoticeIssueLabel(digits);
                      }}
                      placeholder="1, 2, 3…"
                      className="w-full max-w-[8rem] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Chỉ nhập số. Hệ thống tự thêm LẦN / 2ND NOTICE trên tiêu đề thông báo phí.
                    </p>
                  </div>

                  <ApartmentGroupMultiSelect
                    groups={apartmentGroups}
                    selectedGroupIds={selectedGroupIds}
                    onChange={(groupIds) => {
                      setSelectedGroupIds(groupIds);
                      setSelectedApartmentIds([]);
                    }}
                    helperText={t('feeNotificationGroupHint')}
                  />
                  <ApartmentMultiSelect
                    apartments={apartments}
                    selectedApartmentIds={selectedApartmentIds}
                    selectedGroupIds={selectedGroupIds}
                    onChange={setSelectedApartmentIds}
                  />
                  <FeeCategoryMultiSelect
                    categories={feeCategories.map((cat) => ({
                      id: cat.id,
                      name: cat.name,
                      feeCode: cat.feeCode,
                    }))}
                    selectedCategoryIds={selectedFeeCategoryIds}
                    onChange={setSelectedFeeCategoryIds}
                    disabled={feeCategories.length === 0}
                  />
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={onlyOutstanding}
                        onChange={(e) => setOnlyOutstanding(e.target.checked)}
                      />
                      Chỉ căn hộ còn nợ (phí chưa thanh toán trên thông báo)
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={includeAllUnpaidOnNotice}
                        onChange={(e) => setIncludeAllUnpaidOnNotice(e.target.checked)}
                      />
                      Gom tất cả hóa đơn chưa thanh toán lên thông báo (nợ cũ / trả sau)
                    </label>
                  </div>
                </div>
                
                {/* Warning Info */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="w-5 h-5 text-amber-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-amber-800">{t('importantNoteTitle')}</h3>
                      <div className="mt-2 text-sm text-amber-700">
                        <ul className="list-disc list-inside space-y-1">
                          <li>{t('systemWillAutoGenerateText')} <strong>{selectedApartmentIds.length > 0 ? `${selectedApartmentIds.length} căn hộ đã chọn` : selectedGroupIds.length > 0 ? t('selectedApartmentGroupsText') : t('allApartmentsText')}</strong> {t('inBuildingText')}</li>
                          <li>{t('emailWillBeSentText')} <strong>{t('haveEmailAddressText')}</strong></li>
                          <li>{t('billingPeriodText')}: <strong>{selectedPeriodLabel}</strong></li>
                          <li>Không tạo phí mới — chỉ tạo HTML thông báo phí như trang Thông báo phí rồi gửi.</li>
                          <li>{t('operationCannotBeUndoneText')}</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-center">
                  <button
                    onClick={openDeliveryModal}
                    disabled={sending || !selectedBuilding.isActive}
                    className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold rounded-lg hover:from-cyan-600 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                  >
                    {t('sendFeeNotification')}
                  </button>
                </div>

                {!selectedBuilding.isActive && (
                  <p className="mt-3 text-sm text-red-600 text-center">
                    Chung cư này đang không hoạt động. Vui lòng kích hoạt trước khi gửi thông báo.
                  </p>
                )}
              </div>

              {/* Notification History */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('notificationHistory')}</h2>
                
                {loadingHistory ? (
                  <div className="flex justify-center items-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  </div>
                ) : notificationHistory.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('notificationTitle')}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('notificationSentDate')}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('notificationMethod')}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('quantity')}</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('notificationStaffName')}</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {notificationHistory.map((item, index) => (
                          <tr key={item.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {index + 1}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{item.content}</div>
                              <div className="text-sm text-gray-500">{item.billingPeriod}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {new Date(item.sendTime).toLocaleString('vi-VN')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                {formatNotificationMethod(item.method)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{item.quantity}</div>
                              <div className="text-xs text-gray-500">
                                {t('successText')}: {item.successCount} | {t('failureText')}: {item.failureCount}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {item.staffName || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p>{t('noNotificationHistory')}</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <div className="text-center text-gray-500">
                <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t('selectBuildingToContinue')}</h3>
                <p className="text-gray-600">
                  {t('selectBuildingToViewAndSendNotification')}.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <FeeNotificationDeliveryModal
        isOpen={showDeliveryModal}
        mode="MONTHLY_FEE"
        onClose={() => {
          if (!sending) {
            setShowDeliveryModal(false);
          }
        }}
        onConfirm={handleConfirmSend}
        sending={sending}
      />
    </div>
  );
}; 